import clsx from 'clsx';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { downloadDonationSummary } from '../../api/receipt.api';
import { AddIcNumberSheet } from '../../components/donation/AddIcNumberSheet';
import {
  downloadWithToast,
  missingProfileMessage,
  useDonationReceiptDownload
} from '../../hooks/donations/useDonationReceiptDownload';
import {
  useDonationYears,
  useDonationYearSummary,
  useDonorTaxProfile
} from '../../hooks/donations/useDonations';
import { useScrollToTopOnOpen } from '../../hooks/useScrollToTopOnOpen';
import BackHeader from '../../shared/BackHeader';
import type {
  DonationCurrencyTotal,
  DonationLine,
  DonationYearSummary
} from '../../types/donation.types';
import { defaultDonationYear } from '../../utils/donation-format';
import { CharityRow } from './components/CharityRow';
import { DonationsEmptyState } from './components/DonationsEmptyState';
import { DonationYearSummaryCard } from './components/DonationYearSummaryCard';
import { IcNumberBanner } from './components/IcNumberBanner';
import { LoadingState, MessageState } from './components/PageStates';

function YearTabs({
  years,
  selected,
  onSelect
}: {
  years: number[];
  selected: number;
  onSelect: (year: number) => void;
}) {
  return (
    <div role="tablist" aria-label="Year" className="flex gap-2">
      {years.map((year) => (
        <button
          key={year}
          type="button"
          role="tab"
          aria-selected={year === selected}
          onClick={() => onSelect(year)}
          className={clsx(
            'rounded-full px-5 py-2 text-sm font-medium transition-colors',
            year === selected
              ? 'bg-gray-900 text-white'
              : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
          )}
        >
          {year}
        </button>
      ))}
    </div>
  );
}

function ByCharity({
  summary,
  onDownloadReceipt,
  onDownloadAll
}: {
  summary: DonationYearSummary;
  onDownloadReceipt: (donation: DonationLine) => void;
  onDownloadAll: (charityId: string, taxDeductible: boolean) => void;
}) {
  const { charities } = summary;
  // The first row opens expanded, so the latest donations show without a tap.
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(charities.slice(0, 1).map((c) => c.row_key))
  );

  const toggle = (rowKey: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });

  return (
    <section aria-labelledby="by-charity-heading">
      <h2
        id="by-charity-heading"
        className="mb-3 mt-3 text-sm font-bold uppercase tracking-wider text-gray-700"
      >
        By charity
      </h2>
      <div className="space-y-3">
        {charities.map((row) => (
          <CharityRow
            key={row.row_key}
            summary={row}
            year={summary.year}
            expanded={expanded.has(row.row_key)}
            onToggle={() => toggle(row.row_key)}
            onDownloadReceipt={onDownloadReceipt}
            onDownloadAll={() =>
              onDownloadAll(row.charity.id, row.charity.tax_deductible)
            }
          />
        ))}
      </div>
    </section>
  );
}

const MyDonationsPage: React.FC = () => {
  useScrollToTopOnOpen();
  const years = useDonationYears();
  const [selectedYear, setSelectedYear] = useState<number>();
  const year =
    selectedYear ??
    defaultDonationYear(years.data ?? [], new Date().getFullYear());
  const summary = useDonationYearSummary(year);
  const taxProfile = useDonorTaxProfile();
  const receipt = useDonationReceiptDownload();
  const [icSheetOpen, setIcSheetOpen] = useState(false);

  // Settings must add a missing name or email before the IC is worth asking for.
  const openIcSheet = () => {
    const missing = taxProfile.data && missingProfileMessage(taxProfile.data);
    if (missing) toast.error(missing);
    else setIcSheetOpen(true);
  };

  // Only a donor whose nationality has a tax receipt rule, with a tax-deductible donation that
  // year, is asked for the ID.
  const showIcBanner =
    Boolean(taxProfile.data?.id_rule) &&
    taxProfile.data?.has_ic_number === false &&
    (summary.data?.totals.some(
      (t: DonationCurrencyTotal) => t.tax_deductible_total > 0
    ) ??
      false);

  const body = () => {
    if (years.isLoading || summary.isLoading) return <LoadingState />;
    if (years.isError || summary.isError)
      return (
        <MessageState>
          We couldn't load your donations. Please try again later.
        </MessageState>
      );
    if (!year || !summary.data) return <DonationsEmptyState />;

    return (
      <>
        {showIcBanner && <IcNumberBanner onAdd={openIcSheet} />}
        <DonationYearSummaryCard
          summary={summary.data}
          onDownloadSummary={() =>
            downloadWithToast(
              () => downloadDonationSummary(summary.data.year),
              'summary'
            )
          }
        />
        <ByCharity
          // A new year starts with its own expanded row.
          key={summary.data.year}
          summary={summary.data}
          onDownloadReceipt={(donation) =>
            receipt.download(
              donation.donation_ref,
              donation.deductible_for_donor
            )
          }
          onDownloadAll={(charityId, taxDeductible) =>
            receipt.downloadAll(charityId, summary.data.year, taxDeductible)
          }
        />
      </>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <BackHeader title="My Donations" />
      <div className="mx-auto max-w-2xl space-y-3 px-4 py-4 pb-10">
        {year && (years.data?.length ?? 0) > 0 && (
          <YearTabs
            years={years.data ?? []}
            selected={year}
            onSelect={setSelectedYear}
          />
        )}
        {body()}
      </div>

      <AddIcNumberSheet {...receipt.sheet} />
      <AddIcNumberSheet
        open={icSheetOpen}
        purpose="profile"
        onOpenChange={setIcSheetOpen}
      />
    </div>
  );
};

export default MyDonationsPage;
